import React, { useState } from 'react';
import rentalApi, { logClientError } from '../services/api';
import './ReportForm.css';

interface ReportFormProps {
  rentPinId: string;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export const ReportForm: React.FC<ReportFormProps> = ({ rentPinId, onSuccess, onCancel }) => {
  const [reason, setReason] = useState<'IncorrectPrice' | 'InvalidAddress' | 'InappropriateContent' | 'Other'>('IncorrectPrice');
  const [description, setDescription] = useState('');
  const [reporterEmail, setReporterEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (description.trim().length < 10) {
      setError('Description must be at least 10 characters');
      return;
    }

    setLoading(true);

    try {
      await rentalApi.reportRent({
        rentPinId,
        reason,
        description,
        reporterEmail
      });

      onSuccess?.();
    } catch (err: any) {
      const msg = err.message || 'Failed to submit report';
      setError(msg);
      logClientError('report_submit_error', msg, { rentPinId, reason });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="report-form" role="form" aria-label="Report this rental">
      <div className="report-form-header">
        <h3 id="report-form-title">Report This Rental</h3>
        <button type="button" className="report-form-close" onClick={onCancel} aria-label="Close report form"><span aria-hidden="true">✕</span></button>
      </div>
      {error && <div className="error-message" role="alert" id="report-error">{error}</div>}

      <form onSubmit={handleSubmit} aria-labelledby="report-form-title">
        <div className="form-group">
          <label htmlFor="report-reason">Reason for Report <span aria-hidden="true">*</span></label>
          <select
            id="report-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value as typeof reason)}
            aria-required="true"
          >
            <option value="IncorrectPrice">Incorrect Price</option>
            <option value="InvalidAddress">Invalid Address</option>
            <option value="InappropriateContent">Inappropriate Content</option>
            <option value="Other">Other</option>
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="report-description">Description <span aria-hidden="true">*</span></label>
          <textarea
            id="report-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Provide details about why you're reporting this… (min 10 characters)"
            rows={3}
            required
            aria-required="true"
            aria-describedby={error ? 'report-error' : undefined}
            aria-invalid={!!error}
          />
        </div>

        <div className="form-group">
          <label htmlFor="report-email">Your Email <span className="label-optional">(optional)</span></label>
          <input
            id="report-email"
            type="email"
            value={reporterEmail}
            onChange={(e) => setReporterEmail(e.target.value)}
            placeholder="For follow-up communication"
          />
        </div>

        <div className="form-actions">
          <button type="button" onClick={onCancel} className="btn-cancel" disabled={loading}>
            Cancel
          </button>
          <button type="submit" className="btn-submit" disabled={loading} aria-busy={loading}>
            {loading ? 'Submitting…' : 'Submit Report'}
          </button>
        </div>
      </form>

      <p className="info-text"><span aria-hidden="true">⚠️</span> When 5 people report an issue, this listing will be automatically flagged for review.</p>
    </div>
  );
};
